import { EnhancedDimension } from '@factorialco/deprecated-design-system'
import { EmployeeForSelectFragment } from 'generated/resources/graphql'

import { DisplayNameStrategy } from 'modules/core/lib/employeeName'

export type EmployeeForSelectFragmentNoAccess = Omit<EmployeeForSelectFragment, 'access'> & {
  access: { avatar?: string | null }
}

interface EmployeeSelectorCommonProps {
  error?: string
  mandatory?: boolean
  disabled?: boolean
  label?: string
  emptyLabel?: string
  emptyLabelPlural?: string
  width?: EnhancedDimension
  minWidth?: EnhancedDimension
  companyId?: string
  legalEntityIds?: string[]
  employeeIds?: string[]
  withAvatar?: boolean
  onPopoverClose?: () => void
  includeInactive?: boolean
  onlyManagers?: boolean
  onlyMyReportees?: boolean
  onlyDirectReports?: boolean
  excludeCurrentEmployee?: boolean
  excludeIds?: string[]
  hideSearchIcon?: boolean
  hideBorder?: boolean
  hideLabel?: boolean
  defaultOpen?: boolean
  hiddenAvatar?: boolean
  applySearchToGroupIn?: GroupType[]
  /**
   * Controls which grouping options are available in the selector.
   * - 'all': Always available (individual employees)
   * - 'teams' / 'managers' / 'workplaces': Groups by team memberships, managers, default location.
   * @default ['all', 'teams', 'managers'] (not workplaces)
   */
  allowedGroups?: GroupType[]
  defaultGroup?: GroupType
  /**
   * Controls how the employee name is displayed in the selector.
   * - 'preferredOrFull' (default): preferredName + lastName when present; otherwise fullName
   * - 'full': always show firstName + lastName
   */
  displayNameStrategy?: DisplayNameStrategy
}

export interface EmployeeSelectorSingleNoAccess extends EmployeeSelectorCommonProps {
  /**
   * @deprecated please, migrate your code to use employees. Setting this prop as `true` will render an old version of the component.
   */
  usingAccessIds?: false
  multiple?: false
  alwaysOpen?: false
  value?: EmployeeForSelectFragmentNoAccess | null
  onSelect: (employee: EmployeeForSelectFragmentNoAccess | null) => void
  children?: React.ReactNode
  /**
   * Filters the employees depending on a verb to show only the ones you have access to
   */
  verb?: string
}

export interface EmployeeSelectorSingleUsingAccess extends EmployeeSelectorCommonProps {
  /**
   * @deprecated please, migrate your code to use employees. Setting this prop as `true` will render an old version of the component.
   */
  usingAccessIds: true
  multiple?: false
  alwaysOpen?: false
  value?: EmployeeForSelectFragment | null
  onSelect: (employee: EmployeeForSelectFragment | null) => void
}

export interface EmployeeSelectorMultipleNoAccess extends EmployeeSelectorCommonProps {
  /**
   * @deprecated please, migrate your code to use employees. Setting this prop as `true` will render an old version of the component.
   */
  usingAccessIds?: false
  multiple: true
  alwaysOpen?: boolean
  value: EmployeeForSelectFragmentNoAccess[]
  onSelect: (employees: EmployeeForSelectFragmentNoAccess[]) => void
  children?: React.ReactNode
  /**
   * Filters the employees depending on a verb to show only the ones you have access to
   */
  verb?: string
}

export interface EmployeeSelectorMultipleUsingAccess extends EmployeeSelectorCommonProps {
  /**
   * @deprecated please, migrate your code to use employees. Setting this prop as `true` will render an old version of the component.
   */
  usingAccessIds: true
  multiple: true
  alwaysOpen?: boolean
  value: EmployeeForSelectFragment[]
  onSelect: (employees: EmployeeForSelectFragment[]) => void
}

export type EmployeeEntity = {
  id: string
  name: string
  avatar?: string
  expanded?: boolean
  subItems?: EmployeeSubEntity[]
}

export type EmployeeSubEntity = {
  subId: string
  subName: string
  subAvatar?: string
}

export const Groups = {
  ALL: 'all',
  TEAMS: 'teams',
  MANAGERS: 'managers',
  WORKPLACES: 'workplaces',
} as const

export type GroupType = (typeof Groups)[keyof typeof Groups]
